import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxArchive,
  faCheck,
  faDownload,
  faEye,
  faFileCode,
  faFileWord,
  faGavel,
  faGlobe,
  faPaperPlane,
  faPen,
  faPlus,
  faRotateLeft,
  faScaleBalanced,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import Button from "components/Button";
import ConfirmModal from "components/ConfirmModal";
import Loading from "components/Loading";

import localLawService from "api/localLawService";
import {
  ELegalActBox,
  ELegalActWorkflowStatus,
  ILegalActListItem,
} from "dto/LocalLaw/ILocalLaw";
import { ENotificationType, notification } from "utils/notification";

import AdoptModal from "./AdoptModal";
import BipModal from "./BipModal";
import CommentModal from "./CommentModal";
import MetricModal from "./MetricModal";
import NewActModal from "./NewActModal";
import PreviewModal from "./PreviewModal";
import PublicationWizard from "./PublicationWizard";
import SubmitModal from "./SubmitModal";
import SupervisoryRulingModal from "./SupervisoryRulingModal";
import useOpenLegalAct from "./useOpenLegalAct";
import styles from "./styles.module.scss";

// Prawo miejscowe — wspólna lista aktów dla wszystkich skrzynek modułu.
//
// Odwzorowanie list LocalLawProjectsList / LocalLawToAdopt / LocalLawAdopted
// z Kwantum.Documents.List: ta sama siatka wierszy, a zestaw czynności zależy
// od skrzynki i od uprawnień biura rady (pole CanManage z backendu).

interface Props {
  box: ELegalActBox;
  title: string;
  /** Tekst pokazywany, gdy skrzynka jest pusta. */
  emptyText: string;
  /** Przycisk „Nowy projekt" ma sens tylko na liście projektów. */
  allowCreate?: boolean;
  /**
   * Przycisk „Wniosek o publikację" — na liście „Do publikacji", tak jak
   * CreatePublicationRequestCommand w SignAndPublishControl z Magistratu.
   */
  allowPublicationRequest?: boolean;
}

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "—";

/** Nagłówek wiersza: „Uchwała Nr XIV/122/2026" albo sam rodzaj, gdy nie ma numeru. */
const actHeading = (act: ILegalActListItem) =>
  act.ActNumber ? `${act.ActTypeName} Nr ${act.ActNumber}` : act.ActTypeName;

const ActsView = ({
  box,
  title,
  emptyText,
  allowCreate = false,
  allowPublicationRequest = false,
}: Props) => {
  const navigate = useNavigate();
  const [requesting, setRequesting] = useState(false);
  const [text, setText] = useState("");
  // Skrzynka „Do publikacji" prowadzi akt do dziennika urzędowego: treść i metryka
  // są już zamknięte, więc zestaw czynności w wierszu jest tam inny niż w projektach.
  const isPublicationBox = box === ELegalActBox.ToPublish;

  // Zapytanie idzie dopiero po chwili bez pisania — inaczej lista odpytywałaby
  // backend na każdym znaku.
  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState<number | undefined>(undefined);

  useEffect(() => {
    const timer = setTimeout(() => setSearchText(text.trim()), 300);

    return () => clearTimeout(timer);
  }, [text]);

  const [creating, setCreating] = useState(false);
  const [metricFor, setMetricFor] = useState<ILegalActListItem | null>(null);
  const [submitFor, setSubmitFor] = useState<ILegalActListItem | null>(null);
  const [bipFor, setBipFor] = useState<ILegalActListItem | null>(null);
  const [rulingFor, setRulingFor] = useState<ILegalActListItem | null>(null);
  const [adoptFor, setAdoptFor] = useState<ILegalActListItem | null>(null);
  const [previewFor, setPreviewFor] = useState<ILegalActListItem | null>(null);
  const [removing, setRemoving] = useState<ILegalActListItem | null>(null);
  const [commentFor, setCommentFor] = useState<{
    act: ILegalActListItem;
    action: "approve" | "send_back" | "reject" | "to_publish" | "in_force" | "revert";
  } | null>(null);

  const actsQuery = localLawService.useGetActs({
    box,
    text: searchText || undefined,
    type: typeFilter,
  });
  const typesQuery = localLawService.useGetActTypes();

  const { open: openInWord, busy: opening } = useOpenLegalAct();

  const { mutate: approve } = localLawService.useApprove();
  const { mutate: sendBack } = localLawService.useSendBack();
  const { mutate: reject } = localLawService.useReject();
  const { mutate: toPublish } = localLawService.useSendToPublication();
  const { mutate: setInForce } = localLawService.useSetInForce();
  const { mutate: revert } = localLawService.useRevert();
  const { mutate: generateZipx, isLoading: generatingZipx } =
    localLawService.useGenerateZipx();
  const { mutate: removeAct } = localLawService.useRemoveAct();

  const acts = actsQuery.data?.data ?? [];
  const types = typesQuery.data?.data ?? [];

  const refresh = () => actsQuery.refetch();

  const fail = (e: unknown, fallback: string) =>
    notification({
      type: ENotificationType.ERROR,
      title: "Błąd",
      text: errorText(e, fallback),
    });

  const runWorkflow = (
    act: ILegalActListItem,
    action: "approve" | "send_back" | "reject" | "to_publish" | "in_force" | "revert",
    comment: string | null,
  ) => {
    const mutation = {
      approve,
      send_back: sendBack,
      reject,
      to_publish: toPublish,
      in_force: setInForce,
      revert,
    }[action];

    mutation(
      { id: act.Id, request: { Comment: comment } },
      {
        onSuccess: () => {
          setCommentFor(null);
          refresh();
        },
        onError: (e) => fail(e, "Nie udało się wykonać czynności."),
      },
    );
  };

  const doGenerateZipx = (act: ILegalActListItem) => {
    generateZipx(act.Id, {
      onSuccess: () => {
        refresh();
        notification({
          type: ENotificationType.SUCCESS,
          title: "Paczka ZIPX gotowa",
          text: "Paczkę można pobrać z listy i wysłać do dziennika urzędowego.",
        });
      },
      onError: (e) => fail(e, "Nie udało się złożyć paczki ZIPX."),
    });
  };

  const doRemove = () => {
    if (!removing) return;

    const id = removing.Id;
    setRemoving(null);

    removeAct(id, {
      onSuccess: refresh,
      onError: (e) => fail(e, "Nie udało się usunąć projektu."),
    });
  };

  const commentTitle = useMemo(() => {
    switch (commentFor?.action) {
      case "approve":
        return "Akceptacja projektu";
      case "send_back":
        return "Zwrot do poprawy";
      case "reject":
        return "Odrzucenie projektu";
      case "to_publish":
        return "Przekazanie do publikacji";
      case "in_force":
        return "Przeniesienie do aktów obowiązujących";
      case "revert":
        return "Cofnięcie do wcześniejszego etapu";
      default:
        return "";
    }
  }, [commentFor]);

  if (actsQuery.isLoading) return <Loading />;

  return (
    <div className={styles.panel}>
      <div className={styles.topBar}>
        <span className={styles.listHeading}>
          {title} ({acts.length})
        </span>

        <input
          className={styles.filterInput}
          value={text}
          placeholder="Szukaj po tytule lub numerze"
          onChange={(e) => setText(e.target.value)}
        />

        <select
          className={styles.filterSelect}
          value={typeFilter ?? ""}
          onChange={(e) =>
            setTypeFilter(e.target.value === "" ? undefined : Number(e.target.value))
          }
        >
          <option value="">Wszystkie rodzaje</option>
          {types.map((t) => (
            <option key={t.Value} value={t.Value}>
              {t.Name}
            </option>
          ))}
        </select>

        <div className={styles.spacer} />

        {allowPublicationRequest && (
          <Button
            text="Wniosek o publikację"
            element={<FontAwesomeIcon icon={faPaperPlane} />}
            onClick={() => setRequesting(true)}
          />
        )}

        {allowCreate && (
          <Button
            text="Nowy projekt"
            element={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => setCreating(true)}
          />
        )}
      </div>

      <div className={styles.listWrap}>
        {acts.map((act) => (
          <div key={act.Id} className={styles.actRow}>
            <div className={styles.actMain}>
              <span className={styles.actTitle}>
                {actHeading(act)}
                {act.Title ? ` — ${act.Title}` : ""}
              </span>

              <span className={styles.actMeta}>
                {act.Author ? `${act.Author} · ` : ""}
                data aktu: {formatDate(act.Date)}
                {act.AdoptedDate ? ` · uchwalono: ${formatDate(act.AdoptedDate)}` : ""}
                {act.EffectiveFrom
                  ? ` · wchodzi w życie: ${formatDate(act.EffectiveFrom)}`
                  : ""}
              </span>

              <span className={styles.actMeta}>
                autor projektu: {act.CreatedBy ?? "—"}
                {act.AssignedTo ? ` · u: ${act.AssignedTo}` : ""}
                {act.AttachmentsCount
                  ? ` · załączniki: ${act.AttachmentsCount}`
                  : ""}
              </span>

              <div className={styles.badges}>
                <span className={`${styles.badge} ${styles.badgeStatus}`}>
                  {act.StatusName}
                </span>
                <span className={styles.badge}>{act.ActStatusName}</span>
                {act.PublishInBip && (
                  <span className={`${styles.badge} ${styles.badgeBip}`}>BIP</span>
                )}
                {act.HasXml ? (
                  <span className={styles.badge}>XML</span>
                ) : (
                  <span className={`${styles.badge} ${styles.badgeWarn}`}>brak XML</span>
                )}
                {act.HasZipx && <span className={styles.badge}>ZIPX</span>}
                {(act.Repeal || act.PartialRepeal) && (
                  <span className={`${styles.badge} ${styles.badgeWarn}`}>
                    {act.Repeal ? "nieważny" : "nieważny w części"}
                  </span>
                )}
              </div>

              {act.Status === ELegalActWorkflowStatus.ToFix && act.StatusComment && (
                <div className={styles.actComment}>
                  Zwrócono do poprawy: {act.StatusComment}
                </div>
              )}
            </div>

            <div className={styles.rowTools}>
              {/* Edycja treści i metryki należy do wcześniejszych etapów. W „Do publikacji"
                  akt jest już zamknięty redakcyjnie — zostaje podgląd, a gdy trzeba coś
                  poprawić, cofa się go o etap. */}
              {!isPublicationBox && (
                <button
                  type="button"
                  className={styles.toolBtn}
                  title="Otwórz w edytorze Word"
                  disabled={opening}
                  onClick={() => openInWord(act.DocumentId)}
                >
                  <FontAwesomeIcon icon={faFileWord} />
                </button>
              )}

              <button
                type="button"
                className={styles.toolBtn}
                title="Podgląd aktu (treść wraz z załącznikami)"
                onClick={() => setPreviewFor(act)}
              >
                <FontAwesomeIcon icon={faEye} />
              </button>

              {!isPublicationBox && (
                <button
                  type="button"
                  className={styles.toolBtn}
                  title="Metryka aktu"
                  onClick={() => setMetricFor(act)}
                >
                  <FontAwesomeIcon icon={faPen} />
                </button>
              )}

              {act.HasXml && (
                <button
                  type="button"
                  className={styles.toolBtn}
                  title="Pobierz XML aktu"
                  onClick={() =>
                    localLawService
                      .downloadFile(act.Id, "xml", `${actHeading(act)}.xml`)
                      .catch((e) => fail(e, "Nie udało się pobrać XML."))
                  }
                >
                  <FontAwesomeIcon icon={faFileCode} />
                </button>
              )}

              {/* Projekt: przekazanie do akceptacji + usunięcie. */}
              {(act.Status === ELegalActWorkflowStatus.Project ||
                act.Status === ELegalActWorkflowStatus.ToFix) && (
                <>
                  <button
                    type="button"
                    className={styles.toolBtn}
                    title="Przekaż do akceptacji"
                    onClick={() => setSubmitFor(act)}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} />
                  </button>
                  <button
                    type="button"
                    className={`${styles.toolBtn} ${styles.toolBtnDanger}`}
                    title="Usuń projekt"
                    onClick={() => setRemoving(act)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </>
              )}

              {/* Do akceptacji: akceptacja, zwrot, odrzucenie. */}
              {act.Status === ELegalActWorkflowStatus.ToApprove && (
                <>
                  <button
                    type="button"
                    className={styles.toolBtn}
                    title="Akceptuj"
                    onClick={() => setCommentFor({ act, action: "approve" })}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                  </button>
                  <button
                    type="button"
                    className={styles.toolBtn}
                    title="Zwróć do poprawy"
                    onClick={() => setCommentFor({ act, action: "send_back" })}
                  >
                    <FontAwesomeIcon icon={faRotateLeft} />
                  </button>
                  <button
                    type="button"
                    className={`${styles.toolBtn} ${styles.toolBtnDanger}`}
                    title="Odrzuć"
                    onClick={() => setCommentFor({ act, action: "reject" })}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </>
              )}

              {/* Zaakceptowany: uchwalenie (nadanie numeru i daty). */}
              {act.CanManage && act.Status === ELegalActWorkflowStatus.Approved && (
                <button
                  type="button"
                  className={styles.toolBtn}
                  title="Uchwal / wydaj akt"
                  onClick={() => setAdoptFor(act)}
                >
                  <FontAwesomeIcon icon={faGavel} />
                </button>
              )}

              {/* Skrzynka „Do publikacji" prowadzi akt do dziennika urzędowego, więc
                  paczka ZIPX jest tu dostępna dla każdego aktu na liście, nie dopiero
                  po formalnym przekazaniu. Sam wniosek o publikację składa się przyciskiem
                  w pasku nad listą — dotyczy kilku aktów naraz. */}
              {act.CanManage && box === ELegalActBox.ToPublish && (
                <button
                  type="button"
                  className={styles.toolBtn}
                  title="Generuj paczkę ZIPX"
                  disabled={generatingZipx || !act.HasXml}
                  onClick={() => doGenerateZipx(act)}
                >
                  <FontAwesomeIcon icon={faBoxArchive} />
                </button>
              )}

              {act.CanManage && isPublicationBox && (
                <button
                  type="button"
                  className={styles.toolBtn}
                  title="Cofnij do wcześniejszego etapu"
                  onClick={() => setCommentFor({ act, action: "revert" })}
                >
                  <FontAwesomeIcon icon={faRotateLeft} />
                </button>
              )}

              {/* Przekazanie do aktów obowiązujących zamyka ścieżkę — robi się to
                  w „Uchwalonych", a dla aktów formalnie przekazanych także tutaj. */}
              {act.CanManage &&
                (box === ELegalActBox.Adopted ||
                  act.Status === ELegalActWorkflowStatus.ReadyToDispatch) && (
                  <button
                    type="button"
                    className={styles.toolBtn}
                    title="Przenieś do aktów obowiązujących"
                    onClick={() => setCommentFor({ act, action: "in_force" })}
                  >
                    <FontAwesomeIcon icon={faScaleBalanced} />
                  </button>
                )}

              {act.HasZipx && (
                <button
                  type="button"
                  className={styles.toolBtn}
                  title="Pobierz paczkę ZIPX"
                  onClick={() =>
                    localLawService
                      .downloadFile(act.Id, "zipx")
                      .catch((e) => fail(e, "Nie udało się pobrać paczki ZIPX."))
                  }
                >
                  <FontAwesomeIcon icon={faDownload} />
                </button>
              )}

              {act.CanManage && (
                <>
                  <button
                    type="button"
                    className={styles.toolBtn}
                    title="Prezentacja w BIP"
                    onClick={() => setBipFor(act)}
                  >
                    <FontAwesomeIcon icon={faGlobe} />
                  </button>
                  <button
                    type="button"
                    className={styles.toolBtn}
                    title="Rozstrzygnięcie nadzorcze"
                    onClick={() => setRulingFor(act)}
                  >
                    <FontAwesomeIcon icon={faScaleBalanced} />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}

        {!acts.length && <div className={styles.emptyList}>{emptyText}</div>}
      </div>

      {creating && (
        <NewActModal
          types={types}
          onClose={() => setCreating(false)}
          onCreated={(documentId) => {
            setCreating(false);
            refresh();
            openInWord(documentId);
          }}
        />
      )}

      {requesting && (
        <PublicationWizard
          onClose={() => setRequesting(false)}
          onCreated={() => {
            setRequesting(false);
            // Podpis i wysyłkę obsługuje lista wniosków — tam prowadzimy użytkownika.
            navigate("/prawo-miejscowe/wnioski");
          }}
        />
      )}

      {previewFor && (
        <PreviewModal act={previewFor} onClose={() => setPreviewFor(null)} />
      )}

      {metricFor && (
        <MetricModal
          actId={metricFor.Id}
          types={types}
          onClose={() => setMetricFor(null)}
          onSaved={() => {
            setMetricFor(null);
            refresh();
          }}
        />
      )}

      {submitFor && (
        <SubmitModal
          act={submitFor}
          onClose={() => setSubmitFor(null)}
          onSubmitted={() => {
            setSubmitFor(null);
            refresh();
          }}
        />
      )}

      {adoptFor && (
        <AdoptModal
          act={adoptFor}
          onClose={() => setAdoptFor(null)}
          onAdopted={() => {
            setAdoptFor(null);
            refresh();
          }}
        />
      )}

      {bipFor && (
        <BipModal
          actId={bipFor.Id}
          onClose={() => setBipFor(null)}
          onSaved={() => {
            setBipFor(null);
            refresh();
          }}
        />
      )}

      {rulingFor && (
        <SupervisoryRulingModal
          actId={rulingFor.Id}
          onClose={() => setRulingFor(null)}
          onSaved={() => {
            setRulingFor(null);
            refresh();
          }}
        />
      )}

      {commentFor && (
        <CommentModal
          title={commentTitle}
          label="Komentarz (widoczny w metryce aktu)"
          required={commentFor.action === "send_back" || commentFor.action === "reject"}
          onClose={() => setCommentFor(null)}
          onApply={(comment) =>
            runWorkflow(commentFor.act, commentFor.action, comment || null)
          }
        />
      )}

      {removing && (
        <ConfirmModal
          title="Usunąć projekt aktu?"
          text={`${actHeading(removing)} zostanie usunięty razem z dokumentem.`}
          acceptLabel="Usuń"
          cancelLabel="Anuluj"
          showDontShowNextTime={false}
          onAccept={doRemove}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
};

export default ActsView;
